import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  CellText,
  EmptyState,
  Plus,
  Table,
} from '@opendate-app/setlist';

import { daysUntil, relativeDays } from '../../Grants/grantStages';
import { formatCloseDate } from '../../Deals/components/dealStages';
import { reminderCellText } from '../../Grants/grantReminders';
import GrantReminderSettings from './GrantReminderSettings';

const REPORT_PREFIX = 'report:';

const isReport = (milestone) => milestone.key.startsWith(REPORT_PREFIX);
const reportId = (milestone) => Number(milestone.key.slice(REPORT_PREFIX.length));
const stack = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-xl)' };

const isOverdue = (milestone) => !milestone.done && daysUntil(milestone.due_on) < 0;

function StatusBadge({ milestone }) {
  if (milestone.done) return <Badge color="success">Done</Badge>;
  if (isOverdue(milestone)) return <Badge color="error">Overdue</Badge>;
  return <Badge color="gray">Open</Badge>;
}

StatusBadge.propTypes = {
  milestone: PropTypes.shape({
    done: PropTypes.bool,
    due_on: PropTypes.string,
  }).isRequired,
};

function GrantKeyDatesTab({ grant, owners, canManage, patchGrant, openEdit }) {
  const [pending, setPending] = useState({});

  const milestones = (grant.milestones || []).filter((milestone) => milestone.due_on);
  const addDeadline = () => openEdit({ addReportingDeadline: true });

  const toggleDone = async (milestone, checked) => {
    const id = reportId(milestone);
    setPending((current) => ({ ...current, [id]: checked }));
    await patchGrant({
      grant_reporting_deadlines_attributes: [
        { id, completed_at: checked ? new Date().toISOString() : null },
      ],
    });
    setPending((current) => {
      const { [id]: _settled, ...rest } = current;
      return rest;
    });
  };

  const renderStatus = (milestone) => {
    if (!isReport(milestone)) return <StatusBadge milestone={milestone} />;
    const id = reportId(milestone);
    const saving = id in pending;
    return (
      <Checkbox
        label="Done"
        aria-label={`Mark ${milestone.label} done`}
        checked={saving ? pending[id] : Boolean(milestone.done)}
        disabled={!canManage || saving}
        onChange={(event) => toggleDone(milestone, event.target.checked)}
      />
    );
  };

  const columns = [
    {
      key: 'milestone',
      header: 'Milestone',
      render: (milestone) => <CellText primary={milestone.label} supporting={milestone.kind} />,
    },
    {
      key: 'date',
      header: 'Date',
      render: (milestone) => (
        <CellText
          primary={formatCloseDate(milestone.due_on)}
          supporting={milestone.done ? undefined : relativeDays(daysUntil(milestone.due_on))}
        />
      ),
    },
    {
      key: 'reminders',
      header: 'Reminders',
      render: (milestone) => <CellText primary={reminderCellText(milestone, grant)} />,
    },
    { key: 'status', header: 'Status', render: renderStatus },
  ];

  const settings = (
    <GrantReminderSettings grant={grant} owners={owners} canManage={canManage} patchGrant={patchGrant} />
  );

  if (milestones.length === 0) {
    return (
      <div style={stack}>
        <Card header={<CardHeader title="Key dates" />}>
          <EmptyState
            size="sm"
            title="No key dates yet"
            primaryAction={canManage ? { label: 'Add reporting deadline', icon: Plus, onClick: addDeadline } : undefined}
          />
        </Card>
        {settings}
      </div>
    );
  }

  const actions = canManage ? (
    <Button variant="secondary" size="sm" icon={Plus} onClick={addDeadline}>
      Add reporting deadline
    </Button>
  ) : undefined;

  return (
    <div style={stack}>
      <Card padding="none" header={<CardHeader title="Key dates" actions={actions} />}>
        <Table columns={columns} data={milestones} getRowKey={(milestone) => milestone.key} />
      </Card>
      {settings}
    </div>
  );
}

GrantKeyDatesTab.propTypes = {
  grant: PropTypes.shape({
    milestones: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      kind: PropTypes.string,
      due_on: PropTypes.string,
      done: PropTypes.bool,
    })),
  }).isRequired,
  owners: PropTypes.array,
  canManage: PropTypes.bool,
  patchGrant: PropTypes.func.isRequired,
  openEdit: PropTypes.func.isRequired,
};

export default GrantKeyDatesTab;
